import React, { useCallback, useEffect, useState } from 'react';
import { Card, Spinner, Alert, Btn, Input, Select, Badge } from '../../components/UI';
import { api } from '../../utils/api';

const PHASE_LABEL = { E: 'Explore', M: 'Master', D: 'Deliver' };
const PHASE_FILTERS = [['all', 'All phases'], ['E', 'Explore'], ['M', 'Master'], ['D', 'Deliver']];

const textareaStyle = {
  padding: '10px 13px', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)',
  background: 'var(--canvas-white)', color: 'var(--ink)', fontSize: '0.9rem', outline: 'none',
  width: '100%', fontFamily: 'var(--font-body)', resize: 'vertical', minHeight: 64,
};

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

// ToolboxContent — telo Hansen Beck Toolbox-a bez layout/PageHeader omotača,
// da se može ugraditi u Grow → Content Atlas (koji već daje layout i header).
export function ToolboxContent() {
  const [state, setState] = useState({ loading: true, error: null, items: [], canPromote: false });
  const [phase, setPhase] = useState('all');
  const [shelf, setShelf] = useState('standards'); // standards | band | mine

  const load = useCallback(() => {
    api.getSongbook()
      .then(d => setState({ loading: false, error: null, items: d.ingredients, canPromote: !!d.canPromote }))
      .catch(e => setState({ loading: false, error: e.message, items: [], canPromote: false }));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function pack(id) { try { await api.packIngredient(id); load(); } catch (e) { alert(e.message); } }
  async function run(fn) { try { await fn(); load(); } catch (e) { alert(e.message); } }

  if (state.loading) return <Loading />;

  const byPhase = (list) => phase === 'all' ? list : list.filter(i => i.phase === phase);
  const standards = byPhase(state.items.filter(i => i.scope === 'official'));
  const band = byPhase(state.items.filter(i => i.scope === 'community'));
  const mine = byPhase(state.items.filter(i => i.scope === 'personal'));
  const myShared = state.items.filter(i => i.scope === 'community' && i.authorId && i.mine);

  return (
    <>
      {state.error && <Alert type="error">{state.error}</Alert>}

      {/* Shelves + phase filter */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['standards', 'Standards'], ['band', `From the band${band.length ? ` (${band.length})` : ''}`], ['mine', 'My Songbook']].map(([k, label]) => (
            <Btn key={k} size="sm" variant={shelf === k ? 'primary' : 'outline'} onClick={() => setShelf(k)}>{label}</Btn>
          ))}
        </div>
        <Select value={phase} onChange={e => setPhase(e.target.value)} style={{ maxWidth: 180 }}>
          {PHASE_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
      </div>

      {shelf === 'standards' ? (
        <>
          <p style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginBottom: 14 }}>
            The official shelf — the custodians decided, it is Hansen Beck’s, and the creator stays credited. Add what you use to your pack.
          </p>
          {standards.length === 0 ? (
            <p style={{ color: 'var(--ink-faint)', fontSize: '0.88rem' }}>Nothing on this shelf for this phase yet.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
              {standards.map(i => (
                <IngredientCard key={i.id} i={i} onPack={() => pack(i.id)} />
              ))}
            </div>
          )}
        </>
      ) : shelf === 'band' ? (
        <>
          <p style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginBottom: 14 }}>
            Selling practice shared by colleagues, with their name on it — no permission needed to share. Try it, adopt
            what works (add it to your pack); what the band adopts, the custodians can make a Standard.
          </p>
          {band.length === 0 ? (
            <p style={{ color: 'var(--ink-faint)', fontSize: '0.88rem' }}>Nothing shared yet for this phase — share one of yours from My Songbook.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
              {band.map(i => (
                <IngredientCard key={i.id} i={i} footer={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                    <span style={{ fontSize: '0.74rem', color: 'var(--ink-faint)', marginRight: 'auto' }}>{i.adoptions} adopted</span>
                    {i.mine
                      ? <Btn size="sm" variant="ghost" onClick={() => run(() => api.shareIngredient(i.id, true))}>Take back</Btn>
                      : <Btn size="sm" variant={i.packed ? 'primary' : 'outline'} onClick={() => pack(i.id)}>{i.packed ? 'Adopted ✓' : 'Adopt'}</Btn>}
                    {state.canPromote && <Btn size="sm" onClick={() => { if (window.confirm(`Make “${i.name}” a Standard? It becomes Hansen Beck’s, credited to ${i.authorName || 'its creator'}.`)) run(() => api.promoteIngredient(i.id)); }}>Make it a Standard</Btn>}
                  </div>
                } />
              ))}
            </div>
          )}
        </>
      ) : (
        <MineShelf items={mine} shared={myShared} phase={phase} onChanged={load} />
      )}
    </>
  );
}

function IngredientCard({ i, onPack, footer }) {
  return (
    <Card style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.02rem', color: 'var(--ink)' }}>{i.name}</div>
        <Badge status="default">{PHASE_LABEL[i.phase] || i.phase}</Badge>
      </div>
      {i.what && <div style={{ fontSize: '0.86rem', color: 'var(--ink)', fontWeight: 500 }}>{i.what}</div>}
      {i.how && <div style={{ fontSize: '0.84rem', color: 'var(--ink-soft)', lineHeight: 1.5 }}>{i.how}</div>}
      {i.authorName && i.scope !== 'personal' && <div style={{ fontSize: '0.74rem', color: 'var(--ink-faint)' }}>by {i.authorName}</div>}
      {footer || (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
          <span style={{ fontSize: '0.74rem', color: 'var(--ink-faint)' }}>{i.adoptions} in packs</span>
          <Btn size="sm" variant={i.packed ? 'primary' : 'outline'} onClick={onPack}>
            {i.packed ? 'In my pack ✓' : 'Add to my pack'}
          </Btn>
        </div>
      )}
    </Card>
  );
}

function MineShelf({ items, shared, phase, onChanged }) {
  const [form, setForm] = useState({ phase: phase === 'all' ? 'E' : phase, name: '', what: '', how: '' });
  const [editing, setEditing] = useState(null); // id being edited
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      if (editing) await api.updateIngredient(editing, form);
      else await api.createIngredient(form);
      setForm({ phase: phase === 'all' ? 'E' : phase, name: '', what: '', how: '' });
      setEditing(null); onChanged();
    } catch (err) { alert(err.message); } finally { setBusy(false); }
  }
  function edit(i) { setEditing(i.id); setForm({ phase: i.phase, name: i.name, what: i.what || '', how: i.how || '' }); }
  async function remove(id) {
    if (!window.confirm('Delete this note from your Hansen Beck Toolbox?')) return;
    try { await api.deleteIngredient(id); if (editing === id) setEditing(null); onChanged(); }
    catch (e) { alert(e.message); }
  }

  return (
    <>
      <p style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginBottom: 14 }}>
        Your own Songbook — private until you decide otherwise. Capture what works in your own words; share it with the
        band when you want others to try it.{shared.length ? ` You have ${shared.length} note${shared.length > 1 ? 's' : ''} shared with the band.` : ''}
      </p>

      <Card style={{ padding: '16px 18px', marginBottom: 18 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 10 }}>
          {editing ? 'Edit note' : 'Add a note'}
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Select value={form.phase} onChange={e => set('phase', e.target.value)} style={{ maxWidth: 160 }}>
              <option value="E">Explore</option>
              <option value="M">Master</option>
              <option value="D">Deliver</option>
            </Select>
            <Input placeholder="Name" value={form.name} onChange={e => set('name', e.target.value)} style={{ flex: 1, minWidth: 200 }} />
          </div>
          <Input placeholder="What is it? (one line)" value={form.what} onChange={e => set('what', e.target.value)} />
          <textarea placeholder="How you use it…" value={form.how} onChange={e => set('how', e.target.value)} style={textareaStyle} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn type="submit" loading={busy} style={{ alignSelf: 'flex-start' }}>{editing ? 'Save' : 'Add note'}</Btn>
            {editing && <Btn type="button" variant="outline" onClick={() => { setEditing(null); setForm({ phase: phase === 'all' ? 'E' : phase, name: '', what: '', how: '' }); }}>Cancel</Btn>}
          </div>
        </form>
      </Card>

      {items.length === 0 ? (
        <p style={{ color: 'var(--ink-faint)', fontSize: '0.88rem' }}>No notes yet — add your first above.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {items.map(i => (
            <IngredientCard key={i.id} i={i} footer={
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <Btn size="sm" variant="outline" onClick={() => edit(i)}>Edit</Btn>
                <Btn size="sm" variant="outline" onClick={() => remove(i.id)}>Delete</Btn>
                <Btn size="sm" onClick={async () => { try { await api.shareIngredient(i.id); onChanged(); } catch (e) { alert(e.message); } }} style={{ marginLeft: 'auto' }}>Share with the band</Btn>
              </div>
            } />
          ))}
        </div>
      )}
    </>
  );
}
